// Админка: сводка/пользователи, поездки, настройки распознавания и контент
// страницы тарифов — вкладки одной страницы, активная живёт в адресе (?tab=ai),
// а не в стейте: ссылку на конкретную вкладку можно переслать коллеге.
import { useCallback, useEffect, useState, type KeyboardEvent, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import { navigate, useRoute } from '../router';
import { useAuth } from '../auth';
import { errorKey, impersonate } from '../auth';
import type { ApiError } from '../auth';
import { Logo } from '../ui/shell';
import { ConfirmDialog } from '../ui/forms';
import { deleteUser, fetchStats, listChats, listUsers, patchUser } from './api';
import type { AdminStats, AdminUser } from './api';
import { UserStateModal } from './UserStateModal';
import { TripsPanel } from './TripsPanel';
import { AiSettingsPanel } from './AiSettingsPanel';
import { PricesPanel } from './PricesPanel';
import { PricingPanel } from './PricingPanel';
import { LifehacksPanel } from './LifehacksPanel';
import { LogsPanel } from './LogsPanel';
import { ChatsPanel } from './ChatsPanel';
import { LimitsModal } from './LimitsModal';
import styles from './AdminPage.module.css';

const PAGE_SIZE = 25;

// «Цены» стоят сразу за «ИИ» не по алфавиту: модель для веб-поиска и разбора
// страницы у актуализации цен общая с остальным ИИ (models.prices), и настройки
// эти читают вместе.
const TABS = ['users', 'trips', 'chats', 'logs', 'ai', 'prices', 'pricing', 'lifehacks'] as const;
type Tab = (typeof TABS)[number];

/** Неизвестное или отсутствующее значение параметра трактуем как «Пользователи». */
function tabFromQuery(query: URLSearchParams): Tab {
  const value = query.get('tab');
  return TABS.includes(value as Tab) && value !== 'users' ? (value as Tab) : 'users';
}

function tabLabelKey(tab: Tab): string {
  return `admin.tab.${tab}`;
}

function useDateFormat(): (ms: number | null) => string {
  const { lang } = useI18n();
  return useCallback(
    (ms: number | null) => {
      if (!ms) return '—';
      return new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(ms);
    },
    [lang],
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatsRow({ stats }: { stats: AdminStats | null }): ReactElement | null {
  const t = useT();
  if (!stats) return null;
  const cards: Array<[string, string | number]> = [
    ['admin.stats.users', stats.users],
    ['admin.stats.newWeek', stats.newWeek],
    ['admin.stats.activeWeek', stats.activeWeek],
    ['admin.stats.verified', `${stats.verified} / ${stats.users}`],
    ['admin.stats.blocked', stats.blocked],
    ['admin.stats.snapshots', stats.snapshots],
    ['admin.stats.size', formatBytes(stats.stateBytes)],
  ];
  return (
    <div className={styles.stats}>
      {cards.map(([key, value]) => (
        <div key={key} className={styles.stat}>
          <span className={styles.statValue}>{value}</span>
          <span className={styles.statLabel}>{t(key)}</span>
        </div>
      ))}
    </div>
  );
}

/** role="tablist" с ручным управлением фокусом (roving tabindex): стрелки и
    Home/End двигают выбор и фокус вместе, как рекомендует APG для вкладок.
    chatsUnread — неотвеченные сообщения поддержки: счётчик прямо на вкладке
    «Чаты», чтобы их было видно с любой вкладки админки. */
function AdminTabs({
  tab,
  onSelect,
  chatsUnread = 0,
}: {
  tab: Tab;
  onSelect(next: Tab): void;
  chatsUnread?: number;
}): ReactElement {
  const t = useT();

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    const idx = TABS.indexOf(tab);
    let nextIdx = idx;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = TABS.length - 1;
    else return;
    e.preventDefault();
    const next = TABS[nextIdx];
    onSelect(next);
    document.getElementById(`admin-tab-${next}`)?.focus();
  }

  return (
    <div className={styles.tabs} role="tablist" aria-label={t('admin.title')}>
      {TABS.map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          id={`admin-tab-${key}`}
          aria-selected={tab === key}
          aria-controls={`admin-panel-${key}`}
          tabIndex={tab === key ? 0 : -1}
          className={tab === key ? `${styles.tab} ${styles.tabActive}` : styles.tab}
          onClick={() => onSelect(key)}
          onKeyDown={handleKeyDown}
        >
          {t(tabLabelKey(key))}
          {key === 'chats' && chatsUnread > 0 && (
            <span className={styles.tabBadge}>{chatsUnread > 99 ? '99+' : chatsUnread}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function AdminPage(): ReactElement {
  const t = useT();
  const { user, isAdmin, status } = useAuth();
  const formatDate = useDateFormat();
  const route = useRoute();
  const tab = tabFromQuery(route.query);

  function selectTab(next: Tab): void {
    navigate(next === 'users' ? '/admin' : `/admin?tab=${next}`, { replace: true });
  }

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<AdminUser | null>(null);
  // Вход в чужой аккаунт спрашивают отдельно: правки уйдут живому человеку.
  const [entering, setEntering] = useState<AdminUser | null>(null);
  const [limitsFor, setLimitsFor] = useState<AdminUser | null>(null);
  const [chatsUnread, setChatsUnread] = useState(0);

  // Счётчик неотвеченных на вкладке «Чаты»: лёгкий запрос при входе и раз в
  // минуту, плюс при переходе между вкладками (открытие чата гасит счётчик).
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const tick = () => {
      void listChats({ limit: 1 }).then((res) => {
        if (!cancelled && res.ok) setChatsUnread(res.value.unreadTotal);
      });
    };
    tick();
    const timer = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAdmin, tab]);

  const reload = useCallback(async () => {
    const [page, statsRes] = await Promise.all([
      listUsers({ q: search, limit: PAGE_SIZE, offset }),
      fetchStats(),
    ]);
    if (page.ok) {
      setUsers(page.value.users);
      setTotal(page.value.total);
      setError(null);
    } else {
      setError(page.error);
    }
    if (statsRes.ok) setStats(statsRes.value.stats);
  }, [search, offset]);

  useEffect(() => {
    if (isAdmin) void reload();
  }, [isAdmin, reload]);

  // Поиск с задержкой: список перезапрашивается не на каждую букву.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  /**
   * Войти в аккаунт пользователя. Поездку не выбираем — откроется его
   * последняя: с этой стороны админки речь про человека вообще, а не про
   * конкретную поездку (для неё есть кнопка на вкладке «Поездки»).
   */
  async function enterAs(target: AdminUser): Promise<void> {
    setEntering(null);
    const res = await impersonate(target.id);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    // Перезагрузка, а не переход роутером: вместе с сессией меняется всё —
    // пользователь, список поездок, состояние планировщика.
    window.location.assign('/');
  }

  async function act(id: string, action: () => Promise<{ ok: boolean; error?: ApiError }>) {
    setBusyId(id);
    const res = await action();
    setBusyId(null);
    if (!res.ok && res.error) setError(res.error);
    await reload();
  }

  if (status === 'loading') {
    return <div className={styles.page}>{t('auth.busy')}</div>;
  }

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <div className={styles.forbidden}>
          <p>{t(user ? 'admin.forbidden' : 'auth.err.unauthorized')}</p>
          <button type="button" className="btn" onClick={() => navigate('/')}>
            {t('admin.back')}
          </button>
        </div>
      </div>
    );
  }

  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Logo />
        {/* Заголовок идёт за вкладкой: «Пользователи» над настройками
            распознавания читались как ошибка навигации. */}
        <h1 className={styles.title}>{t(tabLabelKey(tab))}</h1>
        <span className={styles.spacer} />
        <button type="button" className="btn btn-sm" onClick={() => navigate('/')}>
          {t('admin.back')}
        </button>
      </header>

      <AdminTabs tab={tab} onSelect={selectTab} chatsUnread={chatsUnread} />

      {tab === 'trips' ? (
        <div id="admin-panel-trips" role="tabpanel" aria-labelledby="admin-tab-trips">
          <TripsPanel stats={stats} />
        </div>
      ) : tab === 'ai' ? (
        <div id="admin-panel-ai" role="tabpanel" aria-labelledby="admin-tab-ai">
          <AiSettingsPanel />
        </div>
      ) : tab === 'prices' ? (
        <div id="admin-panel-prices" role="tabpanel" aria-labelledby="admin-tab-prices">
          <PricesPanel />
        </div>
      ) : tab === 'pricing' ? (
        <div id="admin-panel-pricing" role="tabpanel" aria-labelledby="admin-tab-pricing">
          <PricingPanel />
        </div>
      ) : tab === 'lifehacks' ? (
        <div id="admin-panel-lifehacks" role="tabpanel" aria-labelledby="admin-tab-lifehacks">
          <LifehacksPanel />
        </div>
      ) : tab === 'logs' ? (
        <div id="admin-panel-logs" role="tabpanel" aria-labelledby="admin-tab-logs">
          <LogsPanel />
        </div>
      ) : tab === 'chats' ? (
        <div id="admin-panel-chats" role="tabpanel" aria-labelledby="admin-tab-chats">
          <ChatsPanel />
        </div>
      ) : (
        <div id="admin-panel-users" role="tabpanel" aria-labelledby="admin-tab-users" className={styles.usersPanel}>
          <StatsRow stats={stats} />

          <div className={styles.toolbar}>
            <input
              className="input"
              type="search"
              value={query}
              placeholder={t('admin.search')}
              aria-label={t('admin.search')}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {error && (
            <p className={styles.error} role="alert">
              {t(errorKey(error))}
            </p>
          )}

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('admin.col.user')}</th>
                  <th>{t('admin.col.role')}</th>
                  <th>{t('admin.col.status')}</th>
                  <th>{t('admin.col.created')}</th>
                  <th>{t('admin.col.lastLogin')}</th>
                  <th>{t('admin.col.data')}</th>
                  <th aria-label={t('admin.col.actions')} />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const self = u.id === user?.id;
                  return (
                    <tr
                      key={u.id}
                      className={
                        u.deletedAt != null
                          ? styles.deletedRow
                          : u.status === 'blocked'
                            ? styles.blockedRow
                            : undefined
                      }
                    >
                      <td>
                        <span className={styles.email}>{u.email}</span>
                        {u.name && <span className={styles.name}>{u.name}</span>}
                        {!u.emailVerified && (
                          <span className={styles.badgeWarn}>{t('account.unverified')}</span>
                        )}
                      </td>
                      <td>{t(u.role === 'admin' ? 'admin.role.admin' : 'admin.role.user')}</td>
                      <td>
                        {t(
                          // Удалённый аккаунт — статус отдельный от «Заблокирован»: это
                          // разные состояния (сам ушёл vs его заблокировали), путать
                          // их в списке нельзя.
                          u.deletedAt != null
                            ? 'admin.status.deleted'
                            : u.status === 'blocked'
                              ? 'admin.status.blocked'
                              : 'admin.status.active',
                        )}
                      </td>
                      <td className="mono">{formatDate(u.createdAt)}</td>
                      <td className="mono">{formatDate(u.lastLoginAt)}</td>
                      <td className="mono">
                        {u.workspace ? formatBytes(u.workspace.size) : '—'}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={!u.workspace}
                            onClick={() => setViewing(u)}
                          >
                            {t('admin.action.view')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setLimitsFor(u)}
                          >
                            {t('admin.action.limits')}
                          </button>
                          {/* Собственную роль и статус менять нельзя — сервер
                              отвечает на это 409, поэтому кнопки просто нет. */}
                          {!self && (
                            <>
                              {/* Заблокированному и удалённому войти нельзя:
                                  у них нет действующей сессии, и подделывать
                                  её админка не должна. */}
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={u.status === 'blocked' || u.deletedAt != null}
                                onClick={() => setEntering(u)}
                              >
                                {t('admin.action.enter')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={busyId === u.id}
                                onClick={() =>
                                  act(u.id, () =>
                                    patchUser(u.id, {
                                      status: u.status === 'blocked' ? 'active' : 'blocked',
                                    }),
                                  )
                                }
                              >
                                {t(u.status === 'blocked' ? 'admin.action.unblock' : 'admin.action.block')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={busyId === u.id}
                                onClick={() =>
                                  act(u.id, () =>
                                    patchUser(u.id, { role: u.role === 'admin' ? 'user' : 'admin' }),
                                  )
                                }
                              >
                                {t(u.role === 'admin' ? 'admin.action.demote' : 'admin.action.promote')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                disabled={busyId === u.id}
                                onClick={() => {
                                  if (!window.confirm(t('admin.delete.confirm', { email: u.email }))) return;
                                  void act(u.id, () => deleteUser(u.id));
                                }}
                              >
                                {t('admin.action.delete')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={7} className={styles.empty}>
                      {t('admin.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {total > PAGE_SIZE && (
            <div className={styles.pager}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                {t('admin.prev')}
              </button>
              <span className={styles.pageInfo}>
                {t('admin.page', { from: total === 0 ? 0 : offset + 1, to, total })}
              </span>
              <button
                type="button"
                className="btn btn-sm"
                disabled={to >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                {t('admin.next')}
              </button>
            </div>
          )}

          {viewing && <UserStateModal user={viewing} onClose={() => setViewing(null)} />}

          {limitsFor && (
            <LimitsModal
              user={limitsFor}
              onClose={() => setLimitsFor(null)}
              onSaved={() => void reload()}
            />
          )}

          <ConfirmDialog
            open={entering !== null}
            title={t('admin.action.enter')}
            message={t('impersonate.confirm', { email: entering?.email ?? '' })}
            confirmLabel={t('admin.action.enter')}
            onConfirm={() => {
              if (entering) void enterAs(entering);
            }}
            onCancel={() => setEntering(null)}
          />
        </div>
      )}
    </div>
  );
}
